import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}

// Defined in a module so initial markup is parsed before custom elements upgrade.
// Observe child content as section rendering can connect an element before its children arrive.
class ComplementaryProduct extends HTMLElement {
  connectedCallback() {
    this.removeEventListener('change', this.handleOptionChange);
    this.addEventListener('change', this.handleOptionChange);
    this.contentObserver?.disconnect();
    this.contentObserver = new MutationObserver(() => this.initialize());
    this.contentObserver.observe(this, { childList: true, subtree: true, characterData: true });
    this.initialize();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.handleOptionChange);
    this.contentObserver?.disconnect();
  }

  handleOptionChange = (event) => {
    if (event.target.matches('input[type="radio"]')) this.initialize();
  };

  initialize() {
    const data = this.querySelector('script[type="application/json"]');
    if (!data) return;
    try {
      const productData = JSON.parse(data.textContent);
      this.variants = productData.variants || [];
      this.defaultImage = productData.image || null;
    } catch {
      // A streamed or morphed section may not have inserted the complete JSON yet.
      return;
    }
    this.optionGroups = Array.from(this.querySelectorAll('[data-option-index]'));
    if (this.optionGroups.some((group) => !group.querySelector('input:checked'))) return;
    this.variantInput = this.querySelector('input[name="id"]');
    this.addButton = this.querySelector('[data-complementary-add]');
    this.price = this.querySelector('[data-complementary-price]');
    this.image = this.querySelector('.complementary-product-item__image img');
    this.update();
  }

  update() {
    var selectedOptions = this.optionGroups.map(function (group) {
      var checked = group.querySelector('input:checked');
      return checked ? checked.value : '';
    });
    var variant = this.variants.find(function (item) {
      return (
        item.options.every(function (option, index) {
          return option === selectedOptions[index];
        })
      );
    });
  
    if (this.variantInput) this.variantInput.value = variant && variant.available ? variant.id : '';
    if (this.addButton) this.addButton.disabled = !variant || !variant.available;
    if (this.price && variant && variant.price && this.price.textContent !== variant.price) this.price.textContent = variant.price;
  
    var colorIndex = this.optionGroups.findIndex(function (group) {
      return group.dataset.colorOption === 'true';
    });
    var imageVariant = variant && variant.image ? variant : null;
    if (!imageVariant && colorIndex !== -1) {
      imageVariant = this.variants.find(function (item) {
        return item.image && item.options[colorIndex] === selectedOptions[colorIndex];
      });
    }
    var image = imageVariant ? imageVariant.image : this.defaultImage;
    if (this.image && image) {
      // Remove the old responsive sources so they cannot override the selected image.
      this.image.removeAttribute('srcset');
      this.image.src = image.src;
      this.image.alt = image.alt || '';
      this.image.width = image.width;
      this.image.height = image.height;
    }
  
    this.optionGroups.forEach(
      function (group, groupIndex) {
        Array.prototype.slice.call(group.querySelectorAll('input[type="radio"]')).forEach(
          function (option) {
            var available = this.variants.some(function (item) {
              return (
                item.available &&
                item.options.every(function (value, optionIndex) {
                  return optionIndex === groupIndex
                    ? value === option.value
                    : value === selectedOptions[optionIndex];
                })
              );
            });
            option.setAttribute('aria-disabled', String(!available));
          }.bind(this)
        );
      }.bind(this)
    );
  }
}

if (!customElements.get('complementary-product')) {
  customElements.define('complementary-product', ComplementaryProduct);
}
